import { isEqual } from 'lodash'
import { useMemo, useRef } from 'react'

/**
 * Returns a stable reference to `value` (or a derived value) that only updates
 * when the deeply-compared result changes. This allows callers to pass
 * non-memoized objects while downstream useMemo/useEffect hooks receive stable
 * dependencies.
 *
 * An optional `transform` function can derive a new value from the input;
 * stability is checked against the transform's output.
 */
export default function useStableValue<T>(value: T): T
export default function useStableValue<T, U>(value: T, transform: (value: T) => U): U
export default function useStableValue<T, U = T>(value: T, transform?: (value: T) => U): T | U {
  const transformRef = useRef(transform)
  transformRef.current = transform

  const ref = useRef<T | U>(transform ? transform(value) : value)

  return useMemo(() => {
    const next: T | U = transformRef.current ? transformRef.current(value) : value
    if (!isEqual(ref.current, next)) {
      ref.current = next
    }
    return ref.current
  }, [value])
}
